"use client";

import { useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import Image from "@/components/shared/image";
import { HugeiconsIcon } from "@hugeicons/react";
import MinusSignIcon from "@hugeicons/core-free-icons/MinusSignIcon";
import PlusSignIcon from "@hugeicons/core-free-icons/PlusSignIcon";
import { useColorTheme } from "@/components/providers/color-theme-provider";
import { cn } from "@/lib/utils";

const faqItems = [
  {
    icon: "⚡",
    q: "What can Zapify do for me?",
    a: "Zapify automatically replies to Instagram comments and DMs to capture leads, answer questions, and turn engagement into conversations, even while you're offline.",
  },
  {
    icon: "🛡️",
    q: "Is Zapify safe? Will my account get banned?",
    a: "Yes. Zapify is a Meta approved Tech Provider, runs on Meta's official Instagram API, and uses secure authorization. No bots or unofficial workarounds.",
  },
  {
    icon: "🔒",
    q: "Do I have to share my Instagram password?",
    a: "Never. Connect through Instagram's official secure login (OAuth). Zapify receives permission to automate, not access to your password.",
  },
  {
    icon: "📱",
    q: "Does Zapify work with my Instagram account?",
    a: "Zapify works with Instagram Business and Creator accounts. If you're using a personal account, switching is free and only takes a minute.",
  },
  {
    icon: "⏱️",
    q: "How long does setup take?",
    a: "Most users connect Instagram and launch their first automation in just a few minutes, no technical setup required.",
  },
  {
    icon: "✈️",
    q: "Can I automate Instagram DMs and comment replies?",
    a: "Yes. Trigger DMs and replies automatically from comments, keywords, story replies, and follower actions to respond faster and automate repetitive conversations.",
  },
  {
    icon: "👥",
    q: "How does Zapify help me grow?",
    a: "Zapify turns comments into conversations with automated DMs, lead capture, and follower automation to help grow your account.",
  },
  {
    icon: "🤖",
    q: "Will automated replies sound robotic?",
    a: "No. Customize replies using your own tone, language, and brand voice so responses feel natural and aligned with how you communicate.",
  },
  {
    icon: "⚙️",
    q: "Can I customize the automation rules?",
    a: "Fully. Control triggers, replies, timing, workflows, AI behavior, and audience actions. Set it once and let it run.",
  },
  {
    icon: "📊",
    q: "Can I track how my automations are performing?",
    a: "Yes. Monitor conversations, engagement, leads captured, and automation performance from one simple dashboard.",
  },
  {
    icon: "🟨",
    q: "Is Zapify free? How much does it cost?",
    a: "You can start completely free, no credit card required. Upgrade anytime as your automation needs grow and cancel whenever you want.",
  },
] as const;

const faqSlides = [
  { light: "/temp/faq1.webp", dark: "/temp/faq1_dark.webp" },
  { light: "/temp/faq2.webp", dark: "/temp/faq2_dark.webp" },
  { light: "/temp/faq3.webp", dark: "/temp/faq3_dark.webp" },
  { light: "/temp/faq4.webp", dark: "/temp/faq4_dark.webp" },
  { light: "/temp/faq5.webp", dark: "/temp/faq5_dark.webp" },
] as const;

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState(0);
  const [slideIndex, setSlideIndex] = useState(0);
  const { themeColor } = useColorTheme();
  const sectionRef = useRef<HTMLElement | null>(null);

  return (
    <section ref={sectionRef} className="relative w-full overflow-hidden bg-transparent px-2.5 py-14 sm:px-6 sm:py-20 lg:px-8 lg:py-28" id="faq">
      <div className="pointer-events-none absolute -bottom-40 left-1/2 h-80 w-[82%] -translate-x-1/2 rounded-full bg-[radial-gradient(ellipse_at_center,rgba(251,113,133,0.50)_0%,rgba(244,114,182,0.46)_18%,rgba(168,85,247,0.42)_36%,rgba(59,130,246,0.38)_56%,rgba(34,211,238,0.30)_72%,transparent_84%)] blur-3xl dark:opacity-45" />
      <div className="relative mx-auto w-full max-w-7xl rounded-2xl border border-border/50 bg-card/45 p-3.5 backdrop-blur-[2px] sm:p-6 dark:bg-card/20">
        <div className="grid gap-4 sm:gap-5 lg:grid-cols-[1.02fr_1.25fr]">
          <div data-faq-left className="rounded-2xl bg-transparent p-3.5 sm:p-7">
            <span className={cn(
              "inline-flex rounded-full bg-primary-gradient px-3 py-1 text-xs font-semibold text-white",
              themeColor === "yellow" && "!text-black"
            )}>FAQ</span>
            <h2 className="mt-3 text-[2rem] font-bold tracking-tight text-foreground sm:mt-4 sm:text-5xl">
              <span className="gradient-text">Got questions?</span>
              <br />
              We&apos;ve got answers.
            </h2>
            <p className="mt-3 max-w-md text-[13px] leading-relaxed text-muted-foreground sm:mt-4 sm:text-base">
              Clear answers before you connect your Instagram account or launch your first automation.
            </p>
            <div className="mt-5 overflow-hidden rounded-2xl bg-transparent p-1.5 sm:mt-7 sm:p-2">
              <div className="relative aspect-[16/11] overflow-hidden rounded-xl">
                <Image
                  src={faqSlides[slideIndex].light}
                  alt={`Zapify FAQ interface showing frequently asked questions - slide ${slideIndex + 1}`}
                  width={1200}
                  height={900}
                  className="absolute inset-0 h-full w-full object-cover dark:hidden"
                />
                <Image
                  src={faqSlides[slideIndex].dark}
                  alt={`Zapify FAQ interface dark mode - slide ${slideIndex + 1}`}
                  width={1200}
                  height={900}
                  className="absolute inset-0 hidden h-full w-full object-cover dark:block"
                  aria-hidden
                />
              </div>
              <div className="mt-2 flex items-center justify-center gap-1.5">
                {faqSlides.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    aria-label={`Go to slide ${i + 1}`}
                    onClick={() => setSlideIndex(i)}
                    className={`h-1.5 rounded-full transition-all ${slideIndex === i ? "w-5 bg-primary" : "w-1.5 bg-border/90 dark:bg-zinc-600"}`}
                  />
                ))}
              </div>
            </div>
          </div>

          <div data-faq-right className="space-y-3">
            {faqItems.map((item, index) => {
              const isOpen = openIndex === index;
              return (
                <div
                  key={item.q}
                  data-faq-row
                  onMouseEnter={() => setOpenIndex(index)}
                  className="rounded-2xl border border-border/50 bg-card/90 px-4 py-3.5 transition-colors sm:px-6 sm:py-4 dark:bg-card/80"
                >
                  <button
                    type="button"
                    onClick={() => setOpenIndex((prev) => (prev === index ? -1 : index))}
                    className="flex w-full items-center justify-between gap-3 text-left"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-base leading-none">{item.icon}</span>
                      <p className="text-base font-semibold leading-snug text-foreground sm:text-[1.15rem]">{item.q}</p>
                    </div>
                    <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-full text-muted-foreground">
                      {isOpen ? (
                        <HugeiconsIcon icon={MinusSignIcon} size={16} color="currentColor" aria-hidden />
                      ) : (
                        <HugeiconsIcon icon={PlusSignIcon} size={16} color="currentColor" aria-hidden />
                      )}
                    </span>
                  </button>
                  <div
                    className={`grid transition-all duration-300 ${isOpen ? "mt-2.5 grid-rows-[1fr] opacity-100 sm:mt-3" : "grid-rows-[0fr] opacity-0"}`}
                  >
                    <p className="overflow-hidden pr-3 text-[13px] leading-relaxed text-muted-foreground sm:pr-8 sm:text-[0.95rem]">{item.a}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div data-faq-support className="mt-4 flex flex-col gap-3 rounded-2xl border border-border/50 bg-card/35 px-3.5 py-3.5 backdrop-blur-md sm:mt-5 sm:flex-row sm:items-center sm:justify-between sm:px-6 sm:py-4 dark:bg-card/25">
          <div className="flex items-center gap-3">
            <span className={cn(
              "inline-flex size-10 items-center justify-center rounded-xl bg-primary-gradient text-white",
              themeColor === "yellow" && "!text-black"
            )}>?</span>
            <div>
              <p className="text-[15px] font-semibold text-foreground sm:text-base">Still have questions?</p>
              <p className="text-[13px] text-muted-foreground sm:text-sm">Our team is online 24/7 and ready to help.</p>
            </div>
          </div>
          <Link
            to="/contact"
            className={cn(
              "inline-flex h-10 items-center justify-center rounded-full bg-primary-gradient px-5 text-sm font-bold text-white dark:text-black sm:h-11 sm:px-6",
              "w-fit mx-auto sm:mx-0",
              themeColor === "yellow" && "!text-black"
            )}
          >
            Contact Support
          </Link>
        </div>
      </div>
    </section>
  );
}
